import type { CommonOptionsType } from '@/definition/common/data.d';
import { intlGet } from '@/locales';
import type { ReactNode } from 'react';

/** 初始化策略值 */
export const INITIAL_STRATEGY_VALUE = -1;

export const LIST_TAB_VALUE = {
  /** 默认推送 */
  DEFAULT_PUSH: 0,
  /** 推送实验 */
  EXPERIMENT_PUSH: 1,
  /** 待审核 */
  PENDING_APPROVAL: 2,
} as const;

// export const LIST_TAB_LABEL = {
//   // 默认推送
//   [LIST_TAB_VALUE.DEFAULT_PUSH]: intlGet('PUSH.DEFAULT.TITLE'),
//   // 推送实验
//   [LIST_TAB_VALUE.EXPERIMENT_PUSH]: intlGet('PUSH.APPROVAL.EXPERIMENT_TYPE'),
//   // 待审核
//   [LIST_TAB_VALUE.PENDING_APPROVAL]: intlGet('PUSH.APPROVAL.TITLE.NAME'),
// } as const;

// export const LIST_OPTION: { label: ReactNode | string; id: string }[] = [
//   {
//     label: LIST_TAB_LABEL[LIST_TAB_VALUE.DEFAULT_PUSH],
//     id: String(LIST_TAB_VALUE.DEFAULT_PUSH),
//   },
//   {
//     label: LIST_TAB_LABEL[LIST_TAB_VALUE.EXPERIMENT_PUSH],
//     id: String(LIST_TAB_VALUE.EXPERIMENT_PUSH),
//   },
//   {
//     label: LIST_TAB_LABEL[LIST_TAB_VALUE.PENDING_APPROVAL],
//     id: String(LIST_TAB_VALUE.PENDING_APPROVAL),
//   },
// ];

/** 操作类型枚举（对应变更记录接口的 operation_type）
 * - 0 更新推送（历史数据，保持不变）
 * - 1 重启推送
 * - 2 暂停推送
 * - 3 配置审核（新增）
 * - 4 配置变更申请（新增）
 */
export const OPERATION_TYPE_VALUE = {
  /** 更新推送（历史） */
  UPDATE: 0,
  /** 重启推送 */
  RESUME: 1,
  /** 暂停推送 */
  PAUSE: 2,
  /** 配置审核通过 */
  APPROVE: 3,
  /** 配置审核驳回 */
  REJECT: 4,
} as const;

export type TOperationType =
  (typeof OPERATION_TYPE_VALUE)[keyof typeof OPERATION_TYPE_VALUE];

/** 变更记录弹窗「操作内容」列文案
 * - UPDATE：`默认推送配置更新为：{configName}`（configName 需主色高亮，见组件）
 * - RESUME：`重启推送`
 * - PAUSE：`暂停推送`
 */
export const getOperationContentLabel = (type?: TOperationType): string => {
  if (type === OPERATION_TYPE_VALUE.RESUME)
    return intlGet('PUSH.DEFAULT.RESTART_PUSH');
  if (type === OPERATION_TYPE_VALUE.PAUSE)
    return intlGet('PUSH.DEFAULT.PAUSE_PUSH');
  return intlGet('PUSH.DEFAULT.CONFIG_OPERATION');
};

/** 审核结果 errorType 枚举值 */
export const AUDIT_RESULT_VALUE = {
  /** 审批通过 */
  APPROVED: 1010,
  /** 审批驳回 */
  REJECTED: 1011,
} as const;

/** 实验状态枚举（对应实验列表接口的 exp_status） */
export const EXPERIMENT_STATUS_VALUE = {
  /** 待推送 */
  WAITING: 0,
  /** 推送中 */
  PUSHING: 1,
  /** 完成 */
  COMPLETED: 2,
  /** 已停止 */
  ABORTED: 3,
  /** 草稿（保存草稿后未提交审批，可编辑） */
  DRAFT: 9,
  /** 审批驳回（可重新编辑提交） */
  REJECTED: 6,
  /** 超时未审批（可重新编辑提交） */
  APPROVAL_TIMEOUT: 7,
  /** 跑表超时中止 */
  SEGMENT_TIMEOUT: 8,
} as const;

// 默认推送：推送状态
export const DEFAULT_STATUS_VALUE = {
  // 生效中
  ACTIVE: 1,
  // 已停止
  PAUSE: 0,
};
export type TExperimentStatus =
  (typeof EXPERIMENT_STATUS_VALUE)[keyof typeof EXPERIMENT_STATUS_VALUE];

// 默认推送：推送状态显示文本
export const DEFAULT_STATUS_OPTIONS: CommonOptionsType[] = [
  {
    value: DEFAULT_STATUS_VALUE.ACTIVE,
    labelKey: 'PUSH.DEFAULT.ACTIVE_STATUS',
    theme: 'success',
  },
  {
    value: DEFAULT_STATUS_VALUE.PAUSE,
    labelKey: 'PUSH.DEFAULT.PAUSE_STATUS',
    theme: 'danger',
  },
];

export const EXPERIMENT_STATUS_OPTIONS: CommonOptionsType[] = [
  {
    value: EXPERIMENT_STATUS_VALUE.WAITING,
    labelKey: 'PUSH.EXPERIMENT.WAITING_STATUS',
    theme: 'success',
  },
  {
    value: EXPERIMENT_STATUS_VALUE.PUSHING,
    labelKey: 'PUSH.EXPERIMENT.PUSHING_STATUS',
    theme: 'primary',
  },
  {
    value: EXPERIMENT_STATUS_VALUE.COMPLETED,
    labelKey: 'PUSH.EXPERIMENT.COMPLETED_STATUS',
    theme: 'default',
  },
  {
    value: EXPERIMENT_STATUS_VALUE.ABORTED,
    labelKey: 'PUSH.EXPERIMENT.STOPPED_STATUS',
    theme: 'danger',
  },
  {
    value: EXPERIMENT_STATUS_VALUE.DRAFT,
    labelKey: 'PUSH.EXPERIMENT.DRAFT_STATUS',
    theme: 'default',
  },
  {
    value: EXPERIMENT_STATUS_VALUE.REJECTED,
    labelKey: 'PUSH.EXPERIMENT.REJECTED_STATUS',
    theme: 'danger',
  },
  {
    value: EXPERIMENT_STATUS_VALUE.APPROVAL_TIMEOUT,
    labelKey: 'PUSH.EXPERIMENT.TIMEOUT_STATUS',
    theme: 'danger',
  },
  {
    value: EXPERIMENT_STATUS_VALUE.SEGMENT_TIMEOUT,
    labelKey: 'PUSH.EXPERIMENT.SEGMENT_TIMEOUT_STATUS',
    theme: 'danger',
  },
];

export const getExperimentStatusLabel = (status: number): string => {
  const map: Record<number, string> = {
    [EXPERIMENT_STATUS_VALUE.WAITING]: intlGet(
      'PUSH.EXPERIMENT.WAITING_STATUS',
    ),
    [EXPERIMENT_STATUS_VALUE.PUSHING]: intlGet(
      'PUSH.EXPERIMENT.PUSHING_STATUS',
    ),
    [EXPERIMENT_STATUS_VALUE.COMPLETED]: intlGet(
      'PUSH.EXPERIMENT.COMPLETED_STATUS',
    ),
    [EXPERIMENT_STATUS_VALUE.ABORTED]: intlGet(
      'PUSH.EXPERIMENT.STOPPED_STATUS',
    ),
    [EXPERIMENT_STATUS_VALUE.DRAFT]: intlGet('PUSH.EXPERIMENT.DRAFT_STATUS'),
    [EXPERIMENT_STATUS_VALUE.REJECTED]: intlGet(
      'PUSH.EXPERIMENT.REJECTED_STATUS',
    ),
    [EXPERIMENT_STATUS_VALUE.APPROVAL_TIMEOUT]: intlGet(
      'PUSH.EXPERIMENT.TIMEOUT_STATUS',
    ),
    [EXPERIMENT_STATUS_VALUE.SEGMENT_TIMEOUT]: intlGet(
      'PUSH.EXPERIMENT.SEGMENT_TIMEOUT_STATUS',
    ),
  };
  return map[status] ?? '-';
};

/** 审核倒计时秒数 */
export const AUDIT_COUNTDOWN_SECONDS = 120;

/** 待审核状态值（config_count 请求约定：-1 表示待审核） */
export const PENDING_APPROVAL_STATUS = -1;

/** 判断实验配置是否可编辑（进入创建向导 mode=edit）：
 *  草稿(9)、审批驳回(6)、超时未审批(7) 均可重新编辑并保存/提交；其余状态只读查看 */
export const isExperimentEditable = (status: number): boolean =>
  status === EXPERIMENT_STATUS_VALUE.DRAFT ||
  status === EXPERIMENT_STATUS_VALUE.REJECTED ||
  status === EXPERIMENT_STATUS_VALUE.APPROVAL_TIMEOUT;

/** 判断实验是否已结束（完成、已停止或已进入归档态） */
export const isExperimentFinished = (status: number): boolean =>
  status === EXPERIMENT_STATUS_VALUE.COMPLETED ||
  status === EXPERIMENT_STATUS_VALUE.ABORTED ||
  status === EXPERIMENT_STATUS_VALUE.REJECTED ||
  status === EXPERIMENT_STATUS_VALUE.APPROVAL_TIMEOUT ||
  status === EXPERIMENT_STATUS_VALUE.SEGMENT_TIMEOUT;

// 过滤全量业务信息白名单情况
export enum whitelistEnum {
  // 不过滤白名单（全量）
  NOT_WHITELIST = 0,
  // 过滤推送消息白名单
  FILTER_INFO_PUSH_WHITELIST = 1,
}
